import type { ReportConfig, ReportGlobalFilters } from "../../types";
import { AddActionButton } from "../AddActionButton";
import { ChipList } from "../ChipList";
import { MemberSearchPicker } from "./MemberSearchPicker";

interface ReportFiltersEditorProps {
    report: ReportConfig;
    invoiceNumber: string;
    setInvoiceNumber: (value: string) => void;
    updateReport: (updater: (report: ReportConfig) => ReportConfig) => void;
    addChipValue: (values: string[], value: string) => string[];
    removeChipValue: (values: string[], value: string) => string[];
}

const DEFAULT_REPORT_FILTERS: ReportGlobalFilters = {
    tripActivity: "active-only",
    exclusions: {
        memberIds: [],
        invoiceNumbers: [],
    },
};

function normalizeFilters(filters?: ReportGlobalFilters | null): ReportGlobalFilters {
    return {
        tripActivity: filters?.tripActivity || DEFAULT_REPORT_FILTERS.tripActivity,
        exclusions: {
            memberIds: Array.isArray(filters?.exclusions?.memberIds) ? filters.exclusions.memberIds : [],
            invoiceNumbers: Array.isArray(filters?.exclusions?.invoiceNumbers) ? filters.exclusions.invoiceNumbers : [],
        },
    };
}

export function ReportFiltersEditor({
    report,
    invoiceNumber,
    setInvoiceNumber,
    updateReport,
    addChipValue,
    removeChipValue,
}: ReportFiltersEditorProps) {
    const filters = normalizeFilters(report.globalFilters);
    const updateFilters = (updater: (filters: ReportGlobalFilters) => ReportGlobalFilters) => {
        updateReport((current) => ({ ...current, globalFilters: updater(normalizeFilters(current.globalFilters)) }));
    };

    const addInvoice = () => {
        const value = invoiceNumber.trim();
        if (!value) return;
        updateFilters((current) => ({
            ...current,
            exclusions: {
                ...current.exclusions,
                invoiceNumbers: addChipValue(current.exclusions.invoiceNumbers, value),
            },
        }));
        setInvoiceNumber("");
    };

    return (
        <section className="rc-block rc-report-filters">
            <div className="rc-section-title-row">
                <div>
                    <h3>Report Filters</h3>
                    <p className="rc-block-hint">These filters apply only to this report and are included in its generated SQL.</p>
                </div>
                <span className="rc-badge">Report Level</span>
            </div>

            <div className="rc-report-filter-grid">
                <div className="rc-report-exclusions">
                    <div data-rc-validation="member-exclusions">
                        <MemberSearchPicker
                            values={filters.exclusions.memberIds}
                            onAdd={(value) => updateFilters((current) => ({
                                ...current,
                                exclusions: {
                                    ...current.exclusions,
                                    memberIds: addChipValue(current.exclusions.memberIds, value),
                                },
                            }))}
                            onRemove={(value) => updateFilters((current) => ({
                                ...current,
                                exclusions: {
                                    ...current.exclusions,
                                    memberIds: removeChipValue(current.exclusions.memberIds, value),
                                },
                            }))}
                        />
                    </div>

                    <div className="rc-plan-exclusion" data-rc-validation="invoice-exclusions">
                        <div className="rc-member-head">
                            <div>
                                <p className="rc-hint">Invoice Numbers</p>
                                {filters.exclusions.invoiceNumbers.length > 0 && (
                                    <span>{`${filters.exclusions.invoiceNumbers.length} Invoice${filters.exclusions.invoiceNumbers.length === 1 ? "" : "s"} Excluded`}</span>
                                )}
                            </div>
                        </div>
                        <ChipList
                            values={filters.exclusions.invoiceNumbers}
                            onRemove={(value) => updateFilters((current) => ({
                                ...current,
                                exclusions: {
                                    ...current.exclusions,
                                    invoiceNumbers: removeChipValue(current.exclusions.invoiceNumbers, value),
                                },
                            }))}
                            emptyText="No Invoice Exclusions Added"
                        />
                        <div className="rc-plan-exclusion-card">
                            <label>
                                <span>Add Invoice Number</span>
                                <span className="rc-member-input-wrap">
                                    <span className="material-icons md-18 rc-member-search-icon" aria-hidden="true">receipt_long</span>
                                    <input
                                        value={invoiceNumber}
                                        onChange={(event) => setInvoiceNumber(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                addInvoice();
                                            }
                                        }}
                                        placeholder="Enter invoice number"
                                    />
                                </span>
                            </label>
                            <AddActionButton disabled={!invoiceNumber.trim()} onClick={addInvoice}>Add</AddActionButton>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
}
